import React, { PureComponent } from 'react';

import { Button } from '@eventbrite/eds-button';
import { DataTable } from '@eventbrite/eds-data-table';
import { Icon } from '@eventbrite/eds-icon';
import { BoltFill } from '@eventbrite/eds-iconography';

import CountryHeader from '../CountryHeader';
import CountrySection from '../CountrySection';
import UnitedStatesStateInfo from './UnitedStatesStateInfo';

const COMPARISON_TABLE_ITEMS = [
    {
        id: 'Eligibility',
        eidl: 'Small businesses and non-profits of any size',
        ppp: 'Small businesses and non-profits with no more than 500 employees',
    },
    {
        id: 'Loan Size Determination',
        eidl: 'Based on applicant FICO score, revenue and expenses',
        ppp: 'Expense-based (2.5x monthly payroll, with certification)',
    },
    {
        id: 'Maximum Size',
        eidl: '$2mm',
        ppp: '$10mm',
    },
    {
        id: 'Lender',
        eidl: 'Direct from SBA',
        ppp: 'Eligible lenders',
    },
    {
        id: 'Interest Rate',
        eidl: '3.75% for for-profits, 2.75% for non-profits',
        ppp: '1%',
    },
    {
        id: 'Term',
        eidl: '30 years',
        ppp: '5 years',
    },
    {
        id: 'Relief',
        eidl: 'Advance grant of $10,000 after three days for all eligible applicants',
        ppp: 'Full principal and interest can be forgiven for covered expenses incurred over 24 weeks or through December 31, 2020, whichever comes first',
    },
    {
        id: 'Collateral/Personal Guarantee',
        eidl: 'Personal guarantee required for loan size above $200,000',
        ppp: 'None required',
    },
];

const COMPARISON_TABLE_COLUMNS = [
    {
        fieldName: 'id',
        fieldLabel: '',
        isEditable: false,
        isSortable: false,
    },
    {
        fieldName: 'eidl',
        fieldLabel: 'EIDL',
        isEditable: false,
        isSortable: false,
    },
    {
        fieldName: 'ppp',
        fieldLabel: 'PPP',
        isEditable: false,
        isSortable: false,
    },
];

const CARES_ACT_SECTION = {
    title: 'Coronavirus Aid, Relief, and Economic Security (CARES) Act',
    subSections: [
        {
            title: '',
            content: (
                <div>
                    <a
                        href="https://home.treasury.gov/cares"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        U.S. Treasury CARES Act Website
                    </a>
                </div>
            ),
        },
        {
            title: 'Small Business Paycheck Protection Program (PPP)',
            content: (
                <div>
                    <p>
                        Congress created the{' '}
                        <strong>Paycheck Protection Program (“PPP”)</strong> and
                        authorized up to $659 billion in forgivable loans to
                        small businesses to pay their employees during the
                        COVID-19 crisis.
                        <br />
                        <br />
                        The loan amounts will be forgiven as long as:
                        <ul>
                            <li>
                                The loan proceeds are used to cover payroll
                                costs, and most mortgage interest, rent, and
                                utility costs over the 24-week period after the
                                loan is made; and
                            </li>
                            <li>
                                Employee and compensation levels are maintained;
                                and
                            </li>
                            <li>
                                At least 60% of the loan is used on payroll
                                expense.
                            </li>
                        </ul>
                        Payroll costs are capped at $100,000 on an annualized
                        basis for each employee. Due to likely high
                        subscription, it is anticipated that not more than 40%
                        of the forgiven amount may be for non-payroll costs.
                        <br />
                        <br />
                        Loan payments are deferred until the lender is repaid
                        for forgiveness. If the small business does not apply
                        for forgiveness, the payments are deferred for 10 months
                        after the program ends on Dec 31.
                    </p>
                </div>
            ),
        },
        {
            title: 'Key differences between EIDL and PPP',
            content: (
                <DataTable
                    items={COMPARISON_TABLE_ITEMS}
                    columns={COMPARISON_TABLE_COLUMNS}
                />
            ),
        },
        {
            title: 'Who can apply',
            content: (
                <p>
                    <strong>
                        All businesses with 500 or fewer employees can apply
                    </strong>
                    , including nonprofits, veterans organizations, Tribal
                    business concerns, sole proprietorships, self-employed
                    individuals, and independent contractors.
                </p>
            ),
        },
        {
            title: 'Where to apply',
            content: (
                <p>
                    You can{' '}
                    <strong>apply through any existing SBA 7(a) lender</strong>{' '}
                    or through any federally insured depository institution,
                    federally insured credit union, and Farm Credit System
                    institution that is participating.
                </p>
            ),
        },
        {
            title: 'When to apply',
            content: (
                <p>
                    <strong>August 8, 2020: </strong>application deadline
                </p>
            ),
        },
        {
            title: 'How to apply',
            content: (
                <div>
                    <p>
                        Please note, the Small Business Administration is still
                        updating their website to reflect the new changes
                        effective June 5, 2020. If the material appears outdated
                        on the SBA website, please reach out to the SBA or your
                        lender directly.
                    </p>
                    <br />
                    <p>
                        Complete the&nbsp;
                        <a
                            href="https://www.sba.gov/sites/default/files/2020-06/PPP%20Borrower%20Application%20Form%20%28Revised%20June%2012%202020%29-Fillable-508.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            Paycheck Protection Program loan application
                        </a>
                        &nbsp; and submit it with the required documentation to
                        an&nbsp;
                        <a
                            href="https://www.sba.gov/partners/lenders"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            approved lender
                        </a>
                        &nbsp; that is available to process your application by
                        August 8, 2020. In most instances, the quickest path is
                        to fill out the online forms early and to{' '}
                        <strong>work with your existing banking partner</strong>
                        .
                        <br />
                        <br />
                        For full details, download the&nbsp;
                        <a
                            href="https://home.treasury.gov/system/files/136/PPP%20Borrower%20Information%20Fact%20Sheet.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            Paycheck Protection Program Information Sheet
                        </a>
                        .
                        <br />
                        <br />
                        <a
                            href=" https://www.sba.gov/sites/default/files/2020-06/PPP%20Borrower%20Application%20Form%20%28Revised%20June%2012%202020%29-Fillable-508.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            <Button style="fill">
                                <Icon type={<BoltFill />} />
                                Apply for the Paycheck Protection Program
                            </Button>
                        </a>
                    </p>
                </div>
            ),
        },
        {
            title: 'Tax Provisions for Small Businesses',
            content: (
                <ul>
                    <li>
                        <strong>Employee Retention Tax Credit:</strong> This
                        provision provides a refundable payroll tax credit for
                        50 percent of wages paid by employers to employees
                        during the COVID-19 crisis. The credit is available to
                        employers whose (1) operations were fully or partially
                        suspended, due to a COVID-19-related shut-down order, or
                        (2) gross receipts declined by more than 50 percent when
                        compared to the same quarter in the prior year.
                    </li>
                    <li>
                        <strong>Employer Payroll Tax Delay:</strong> This
                        provision allows employers and self-employed individuals
                        to defer payment of the employer share of the Social
                        Security tax they otherwise are responsible for paying
                        to the federal government with respect to their
                        employees.
                    </li>
                    <li>
                        <strong>Modification For Net Operating Losses:</strong>
                        &nbsp; This provision relaxes the limitations on a
                        company’s use of losses. Net Operating Losses (NOL) are
                        currently subject to a taxable-income limitation, and
                        they cannot be carried back to reduce income in a prior
                        tax year. The provision provides that an NOL arising in
                        a tax year beginning in 2018, 2019, or 2020 can be
                        carried back five years.
                    </li>
                    <li>
                        <strong>
                            Modification of Limitation On Business Interest:
                        </strong>
                        &nbsp; The provision temporarily increases the amount of
                        interest expense businesses are allowed to deduct on
                        their tax returns, by increasing the 30-percent
                        limitation to 50 percent of taxable income (with
                        adjustments) for 2019 and 2020.
                    </li>
                </ul>
            ),
        },
        {
            title: 'Individual Taxpayer and Unemployment Relief',
            content: (
                <ul>
                    <li>
                        <strong>Recovery Rebates:</strong> One-time checks of
                        $1,200 to Americans with adjusted gross income up to
                        $75,000 for individuals and $150,000 for married
                        couples. Individuals and couples are eligible for an
                        additional $500 per child.
                    </li>
                    <li>
                        <strong>Pandemic Unemployment Assistance:</strong>&nbsp;
                        Temporary Pandemic Unemployment Assistance program
                        through December 31, 2020 to provide payment to those
                        not traditionally eligible for unemployment benefits
                        (self-employed, independent contractors, those with
                        limited work history, and others) who are unable to work
                        as a direct result of the coronavirus public health
                        emergency.
                    </li>
                    <li>
                        <strong>Increased Unemployment Benefits:</strong>&nbsp;
                        Additional $600 per week payment to each recipient of
                        unemployment insurance or Pandemic Unemployment
                        Assistance for up to four months.
                    </li>
                    <li>
                        <strong>Paid Leave Provisions:</strong> Limits employer
                        paid leave obligations to no more than $200 per day and
                        $10,000 in aggregate for each employee covered. Caps for
                        emergency paid sick leave are $511 per day and $5,110 in
                        aggregate per employee. Employees laid off after March
                        1, 2020 are required to have access to paid family and
                        medical leave if they are rehired by the employer. The
                        bill allows employers to receive an advance payroll tax
                        credit for required paid sick leave.
                    </li>
                </ul>
            ),
        },
    ],
    links: [
        {
            title: 'CARES Act',
            url: 'https://www.congress.gov/bill/116th-congress/house-bill/748/text',
        },

        {
            title: 'Tax Benefit Information',
            url: 'http://www.IRS.gov',
        },
    ],
};

const SBA_EIDL_SECTION = {
    title: 'SBA Economic Injury Disaster Loans (EIDL) & Loan Advance for COVID-19',
    subSections: [
        {
            title: '',
            content: (
                <p>
                    On March 6, the U.S. Congress passed a COVID-19 preparedness
                    and response supplemental funding bill that designated
                    COVID-19 as a disaster under the Small Business
                    Administration (SBA) and provided $20 million to support the
                    SBA’s administration of loan subsidies to small businesses
                    of up to $2 million per loan.
                    <br />
                    <br />
                    On March 27, Congress passed the CARES Act, adding $10
                    billion to the loan program and waiving or relaxing several
                    loan eligibility requirements.
                </p>
            ),
        },
        {
            title: 'SBA Loans Quick Summary',
            content: (
                <ol>
                    <li>
                        Small business owners in&nbsp;
                        <strong>
                            all U.S. states, Washington D.C., and territories
                        </strong>
                        &nbsp; are eligible.
                    </li>
                    <li>
                        Loans offer <strong>up to $2 million</strong> in
                        assistance.
                    </li>
                    <li>
                        The SBA will make&nbsp;
                        <strong>
                            $10,000 advances to applicants within 3 days
                        </strong>
                        &nbsp; of applying to support payroll, sick leave, and
                        other debt obligations; these amounts are not subject to
                        repayment obligations.
                    </li>
                    <li>
                        Loans for businesses will have an interest rate of
                        3.75%, and for nonprofits a rate of 2.75%.
                    </li>
                    <li>
                        SBA offers loans with long-term repayments in order to
                        keep payments affordable, up to a maximum of 30 years.
                        Terms are determined on a case-by-case basis, based upon
                        each borrower’s ability to repay.
                    </li>
                    <li>
                        Loans can be approved&nbsp;
                        <strong>on the basis of a credit score</strong>; tax
                        returns are no longer required.
                    </li>
                    <li>
                        For loans under $200,000, the requirement for personal
                        collateral is waived.
                    </li>
                    <li>
                        For existing SBA loans, the government is
                        providing&nbsp;
                        <strong>
                            $17 billion to pay all principal, interest, and
                            fees, for six months
                        </strong>
                        , to small businesses negatively impacted by COVID-19.
                    </li>
                    <li>
                        SBA loans may be subject to other rules; check the&nbsp;
                        <a
                            href="https://www.sba.gov/funding-programs/loans"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            SBA website
                        </a>
                        &nbsp;for the most current program information.
                    </li>
                </ol>
            ),
        },
        {
            title: 'Who can apply for an EIDL loan',
            content: (
                <p>
                    Small businesses, startups, nonprofits, sole-proprietors,
                    and freelancers.
                </p>
            ),
        },
        {
            title: 'Qualifying for an EIDL loan',
            content: (
                <p>
                    Be sure to consult the EIDL loan application forms for
                    specific requirements, but in general, you must:
                    <ul>
                        <li>be a registered for-profit business</li>
                        <li>
                            be physically located and operates in the U.S. or
                            its territories
                        </li>
                        <li>
                            have invested your own time or money into the
                            business
                        </li>
                    </ul>
                </p>
            ),
        },
        {
            title: 'Applying for an EIDL loan',
            content: (
                <div>
                    <p>
                        You can apply online on the&nbsp;
                        <a
                            href="https://covid19relief.sba.gov/#/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eds-text-weight--heavy"
                        >
                            SBA Disaster website
                        </a>
                        . You’ll need the following:
                    </p>
                    <ul>
                        <li>Business plan</li>
                        <li>Amount and proposed use of funds</li>
                        <li>Credit history</li>
                        <li>
                            Financial projections, and how you will pay back the
                            loan
                        </li>
                        <li>
                            Collateral, such as a home, car, inventory, or other
                            property you own, for loans over $200,000
                        </li>
                        <li>
                            Industry experience, while not required, can drive
                            lender confidence
                        </li>
                    </ul>
                    <a
                        href="https://covid19relief.sba.gov/#/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        <Button style="fill">
                            <Icon type={<BoltFill />} />
                            Apply now for an SBA EIDL loan
                        </Button>
                    </a>
                </div>
            ),
        },
        {
            title: 'Additional Resources',
            content: (
                <div>
                    <a
                        href="https://disasterloan.sba.gov/ela/Information/PaperForms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        Disaster Assistance Application
                    </a>
                    <br />
                    <a
                        href="https://www.sba.gov/sites/default/files/resource_files/Disaster_Playbook_One-Pager_8.1.18_logo_V1_.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        SBA Disaster Playbook
                    </a>
                </div>
            ),
        },
        {
            title: 'Points of Contact',
            content: (
                <p>
                    For additional information, please contact the SBA disaster
                    assistance customer service center:
                    <br />
                    Call 1-800-659-2955 (TTY: 1-800-877-8339)
                    <br />
                    E-mail disastercustomerservice@sba.gov
                </p>
            ),
        },
    ],
    links: [],
};

const PRIVATE_PROGRAMS_SECTION = {
    title: 'Private Company Programs',
    subSections: [
        {
            title: 'Bacardi #RaiseYourSpirits Campaign',
            infoLink:
                'https://www.bacardilimited.com/bacardi-limited-commits-3-million-to-support-the-bar-and-restaurant-industry/',
            content: (
                <p>
                    $3 million in relief to bars and restaurants affected by the
                    COVID-19 shutdowns.
                </p>
            ),
        },
        {
            title: 'Facebook Small Business Grants Program',
            infoLink: 'https://www.facebook.com/business/boost/grants',
            content: (
                <p>
                    Facebook is offering $100M in cash grants and ad credits for
                    up to 30,000 eligible small businesses in over 30 countries.
                </p>
            ),
        },
        {
            title: 'GoFundMe Small Business Relief Fund',
            infoLink: 'https://www.gofundme.com/f/smallbusinessrelieffund',
            content: (
                <p>
                    The GoFundMe.org Small Business Relief Fund will provide
                    micro-grants to qualifying small businesses negatively
                    impacted by the COVID-19 pandemic. The Small Business Relief
                    Fund is part of the Small Business Relief Initiative : a
                    program supported by GoFundMe, Yelp, and Intuit QuickBooks.
                    The Small Business Relief Initiative will supply financial
                    assistance and support to businesses across the country by
                    providing grants, tools, and resources to help during the
                    crisis.
                </p>
            ),
        },
        {
            title: 'Kiva Lending',
            infoLink:
                'https://www.kiva.org/blog/support-local-businesses-during-the-coronavirus-pandemic',
            applicationLink: 'https://www.kiva.org/borrow',
            content: (
                <div>
                    <p>
                        U.S. applicants for a Kiva loan have access to expanded
                        eligibility for a Kiva loan, loans up to $15,000 at 0%
                        interest, and a grace period for new borrowers of up to
                        6 months.
                    </p>
                    <p>
                        Once you complete the application, a member of the Kiva
                        U.S. team will reach out to you to better understand
                        your business and ask any follow up questions within 10
                        - 15 business days. Submitting a great photo and
                        business story will help your application make it
                        through the review process more quickly.
                    </p>
                    <p className="eds-l-mar-top-2">
                        <strong>Qualifications:</strong>
                    </p>
                    <ul>
                        <li>
                            You and your business must be based in the United
                            States.
                        </li>
                        <li>You must be over 18 years old.</li>
                        <li>
                            You must be using the loan for business purposes.
                        </li>
                        <li>
                            Your business must not be engaged in any of the
                            following activities: multi-level marketing / direct
                            sales; illegal activities (e.g. gambling, scams); or
                            pure financial investing (e.g. stocks)
                        </li>
                        <li>
                            You cannot currently be in foreclosure, bankruptcy,
                            or under any liens.
                        </li>
                        <li>
                            You must be willing to demonstrate your social
                            capital by having a small number of your friends and
                            family make a loan to you.
                        </li>
                    </ul>
                    <p>
                        <strong>How quickly can I get funded?</strong>
                    </p>
                    <p>
                        Once your loan is approved for fundraising, you will
                        have 15 days to raise support from your own network
                        during the Private Fundraising Period, and then 30 days
                        to publicly fundraise with Kiva’s wider network of
                        lenders.
                    </p>
                </div>
            ),
        },
        {
            title: 'Plastiq Financing',
            infoLink: 'https://www.plastiq.com/eventbrite/',
            content: (
                <p>
                    Use your existing credit with Plastiq to pay your lease,
                    utilities, contractors, and vendors by using your business
                    credit card, even if they don&apos;t accept credit cards.
                    You&apos;ll pay a 2.5% fee and repay the expense in 30+
                    days.
                </p>
            ),
        },
        {
            title: 'Shopify Small Business Financing',
            infoLink: 'https://www.shopify.com/covid19',
            content: (
                <p>
                    $200 million in small business funding via Shopify Capital.
                </p>
            ),
        },
        {
            title: 'Verizon Small Business Recovery Fund',
            applicationLink:
                'https://www.lisc.org/covid-19/verizon-small-business-recovery-fund/',
            content: (
                <p>
                    Thanks to a $2.5 million investment from Verizon, Local
                    Initiatives Support Corporation (LISC) is offering grants to
                    help small businesses fill urgent financial gaps until they
                    can resume normal operations or until other more permanent
                    financing becomes available.
                </p>
            ),
        },
    ],
    links: [],
};

const US_INFO = {
    countryName: 'United States',
    lastUpdated: 'March 27, 2020',
    introSection: (
        <div>
            <p>
                On March 27, 2020, Congress passed and the President signed the
                Coronavirus Aid, Relief, and Economic Security (CARES) Act -- a
                landmark $2 trillion stimulus package addressing the impact of
                COVID-19.
            </p>
            <br />
            <p>
                On June 5, 2020, the President signed the Paycheck Protection
                Program Flexibility Act into law, legislation designed to build
                on and improve the CARES Act’s small business relief programs.
                These relief programs include:
            </p>
            <ul className="eds-l-mar-top-8 eds-l-mar-bot-4">
                <li>
                    <strong>Paycheck Protection Program:</strong> A $659 billion
                    forgivable loan program for small businesses and nonprofits
                    to maintain existing workforce and help pay for other
                    expenses like rent, mortgage, and utilities.
                </li>
                <li>
                    <strong>Funding for Disaster Loans and Grants:</strong> An
                    additional $10 billion in funding for SBA Economic Injury
                    Disaster Loans (EIDL), with emergency grants of up to
                    $10,000 to provide immediate relief for small business
                    operating costs.
                </li>
                <li>
                    <strong>Debt Relief for existing SBA loans:</strong> $17
                    billion for the Small Business Administration (SBA) to cover
                    6 months of payments for small businesses with existing SBA
                    loans, including 7(a), Community Advantage, 504, and
                    Microloan programs.
                </li>
            </ul>
            <p>
                <strong>
                    See below for more details on the relief resources, and
                    check back for updates on how to take advantage of these
                    programs.&nbsp;
                    <a
                        href="https://www.sba.gov/sites/default/files/resource_files/Disaster_Playbook_One-Pager_8.1.18_logo_V1_.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        Learn more
                    </a>
                    &nbsp; about SBA Economic Injury Disaster Loans -- and&nbsp;
                    <a
                        href="https://covid19relief.sba.gov/#/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eds-text-weight--heavy"
                    >
                        click here
                    </a>
                    &nbsp; to apply.
                </strong>
            </p>
        </div>
    ),
    sections: [CARES_ACT_SECTION, SBA_EIDL_SECTION],
};

class UnitedStatesInfo extends PureComponent {
    render() {
        const { defaultRegionSelected } = this.props;

        return (
            <div className="eds-g-group">
                <CountryHeader info={US_INFO} />
                <CountrySection section={CARES_ACT_SECTION} />
                <CountrySection section={SBA_EIDL_SECTION} />
                <UnitedStatesStateInfo
                    defaultRegionSelected={defaultRegionSelected}
                />
                <CountrySection section={PRIVATE_PROGRAMS_SECTION} />
            </div>
        );
    }
}

export default UnitedStatesInfo;
